import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { TWButton } from '../../../components/ui';
import squareCatalogApi from '../../../services/squareCatalogApi';
import squareErrorToast from '../../../components/menu/square-shared/squareErrorToast';
import SyncHistoryModal from './sync-history-modal';

const SquareMenuTools = ({ globalCompany, storeList, squareLocationList }) => {
  const navigate = useNavigate();
  const integratedStores = storeList.filter((store) =>
    squareLocationList.some(
      (loc) => loc.storeId === store.id && loc.isIntegrated
    )
  );

  const [syncing, setSyncing] = useState(false);
  const [reconnectNeeded, setReconnectNeeded] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const loadSyncStatus = async () => {
    if (!globalCompany?.id) return;
    try {
      const res = await squareCatalogApi.syncStatus(globalCompany.id);
      const data = res.data.data;
      // A sync-in-progress with no prior sync ever recorded comes back as
      // inprogress:true with a DEFAULT (unset) lastsyncat — "0001-01-01...".
      // Treat that as "no last sync" so it doesn't render a garbage date.
      const hasRealLastSync = data?.lastsyncat && !data.lastsyncat.startsWith('0001');
      setLastSync(hasRealLastSync ? data : null);
    } catch (e) {
      setLastSync(null);
    }
  };

  useEffect(() => {
    loadSyncStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany?.id]);

  const handleApiError = (err) => {
    if (squareErrorToast(err)) {
      setReconnectNeeded(true);
    }
  };

  const onClickSync = async () => {
    try {
      setSyncing(true);
      const res = await squareCatalogApi.sync({ companyid: globalCompany.id });
      const s = res.data.data;
      message.success(
        <span>
          {`Synced — ${s.pricesupdated} prices, ${s.namesupdated} names`}
          {s.addedhidden > 0 && (
            <>
              {', '}
              <span
                className="cursor-pointer underline"
                onClick={() => {
                  message.destroy();
                  navigate('/dashboard/menus?review=1');
                }}
              >
                {`${s.addedhidden} added hidden — review in the menu editor`}
              </span>
            </>
          )}
        </span>,
        8
      );
      loadSyncStatus();
    } catch (err) {
      handleApiError(err);
    } finally {
      setSyncing(false);
    }
  };

  if (integratedStores.length === 0) return null;

  return (
    <div className="mt-6 border-t pt-4 dark:border-slate-600">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200">
        Menu
      </h3>
      <div className="mt-1">
        <p className="text-sm font-semibold text-gray-800 dark:text-slate-200">
          Auto-sync: on
        </p>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
          {lastSync ? (
            <>
              Last synced {moment(
                lastSync.lastsyncat.endsWith('Z')
                  ? lastSync.lastsyncat
                  : lastSync.lastsyncat + 'Z'
              ).fromNow()}{' '}
              ({lastSync.triggertype === 20 ? 'auto' : 'manual'})
              {lastSync.summary && (
                <>
                  {' '}— {lastSync.summary.pricesupdated} prices,{' '}
                  {lastSync.summary.namesupdated} names
                  {lastSync.summary.addedhidden > 0 && (
                    <>, {lastSync.summary.addedhidden} added hidden</>
                  )}
                </>
              )}
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                className="ml-2 text-xs font-semibold text-blue-600 underline hover:text-blue-700 dark:text-blue-400"
              >
                History
              </button>
            </>
          ) : (
            <>No syncs yet.</>
          )}
        </p>
      </div>
      {reconnectNeeded && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-300">
          Reconnect Square to grant catalog access (a new permission,
          ITEMS_READ, is required for menu import).
        </p>
      )}
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
        <TWButton
          variant="default"
          label={syncing ? 'Syncing…' : 'Sync now'}
          disabled={syncing}
          onClick={onClickSync}
        />
      </div>
      <SyncHistoryModal open={showHistory} onClose={() => setShowHistory(false)} />
    </div>
  );
};

export default SquareMenuTools;
